/**
 * Collection info tile injector.
 *
 * Reads every <template data-collection-info-tile> in the page,
 * and injects each tile's content into the product grid as a <li> element.
 * All tile styles occupy one grid column (same footprint as a product card).
 *
 * Styles live in assets/dev-collection-info-tile.css (global asset), not
 * {% stylesheet %}, so they load only when the collection has visible tiles.
 *
 * Tiles are re-evaluated after each grid morph so placements whose target
 * product is not yet in the DOM are injected when it appears.
 */

const TILE_ATTR = 'data-collection-info-tile';
const TILE_LI_CLASS = 'product-grid__item--info-tile';
const PRODUCT_ITEM_SELECTOR = '.product-grid__item:not(.product-grid__item--info-tile)';
const GRID_SELECTOR = '[data-testid="product-grid"]';

/** @type {boolean} */
let injectScheduled = false;

/**
 * Build a <li> wrapper containing a clone of the tile template content.
 * @param {HTMLTemplateElement} tpl
 * @returns {HTMLLIElement | null}
 */
function buildTileLi(tpl) {
  const tileId = tpl.dataset.tileId;
  if (!tileId) return null;

  const li = document.createElement('li');
  li.className = `product-grid__item ${TILE_LI_CLASS}`;
  li.dataset.tileId = tileId;
  // Stable id keeps Horizon morph from mistaking this node for a product card.
  li.id = `info-tile-${tileId}`;
  li.appendChild(tpl.content.cloneNode(true));
  return li;
}

/**
 * Inject tiles into the grid.
 * Works by snapshotting the current product items list, then inserting
 * each tile after the Nth product (or before all products for position 0).
 * Processing is done from the highest position to the lowest so earlier
 * insertions don't shift the reference indices of later ones.
 */
function injectTiles() {
  const grid = document.querySelector(GRID_SELECTOR);
  if (!(grid instanceof HTMLUListElement)) return;

  // The "organic" layout assigns column spans via :nth-of-type cycles
  // (see snippets/product-grid.liquid). Interleaving a tile would shift every
  // following card's nth-of-type and break that pattern, so skip injection there.
  if (grid.classList.contains('product-grid--organic')) return;

  const templates = Array.from(document.querySelectorAll(`template[${TILE_ATTR}]`)).filter(
    (el) => el instanceof HTMLTemplateElement
  );
  if (!templates.length) return;

  const injectedTileIds = new Set(
    Array.from(grid.querySelectorAll(`.${TILE_LI_CLASS}[data-tile-id]`))
      .filter((el) => el instanceof HTMLElement)
      .map((el) => el.dataset.tileId)
      .filter((tileId) => tileId != null)
  );

  const pendingTiles = templates
    .filter((tpl) => {
      const tileId = tpl.dataset.tileId;
      return tileId != null && !injectedTileIds.has(tileId);
    })
    .map((tpl) => ({
      insertAfter: parseInt(tpl.dataset.insertAfter ?? '', 10) || 0,
      tpl,
    }));

  if (!pendingTiles.length) return;

  const productItems = Array.from(grid.querySelectorAll(PRODUCT_ITEM_SELECTOR));

  pendingTiles.sort((a, b) => b.insertAfter - a.insertAfter);

  for (const { insertAfter, tpl } of pendingTiles) {
    if (insertAfter > productItems.length) continue;

    const li = buildTileLi(tpl);
    if (!li) continue;

    if (insertAfter === 0) {
      const firstProduct = productItems[0];
      if (firstProduct) {
        grid.insertBefore(li, firstProduct);
      } else {
        grid.appendChild(li);
      }
    } else {
      const refItem = productItems[Math.min(insertAfter, productItems.length) - 1];
      if (refItem) {
        refItem.insertAdjacentElement('afterend', li);
      } else {
        grid.appendChild(li);
      }
    }
  }
}

/**
 * Debounce re-injection to one frame; skip mutations caused by our own tile nodes.
 * @param {MutationRecord[]} mutations
 */
function scheduleInject(mutations) {
  const hasProductGridChange = mutations.some((record) => {
    const nodes = [...record.addedNodes, ...record.removedNodes];
    return nodes.some(
      (node) =>
        node instanceof Element &&
        !node.classList.contains(TILE_LI_CLASS) &&
        node.querySelector?.(`.${TILE_LI_CLASS}`) == null
    );
  });

  if (!hasProductGridChange) return;

  if (injectScheduled) return;
  injectScheduled = true;

  requestAnimationFrame(() => {
    injectScheduled = false;
    injectTiles();
  });
}

const observer = new MutationObserver(scheduleInject);

function observeGrid() {
  const grid = document.querySelector(GRID_SELECTOR);
  if (grid) {
    observer.observe(grid, { childList: true, subtree: false });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      injectTiles();
      observeGrid();
    },
    { once: true }
  );
} else {
  injectTiles();
  observeGrid();
}
